import React, { type FC } from 'react';
import { CardShell } from './card-shell';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, asArray } from './types';
import { normalizeAvatarUrl } from '../../../lib/avatar-url';

const MAX_DISPLAY = 8;
const defaultAvatar = require('../../../../assets/default-avatar.png');

interface CandidateItem {
  userId?: string;
  name?: string;
  title?: string;
  location?: string;
  skills?: string[];
  matchedSkills?: string[];
  matchScore?: number;
  openToWork?: boolean;
  avatarUrl?: string;
}

export const CandidateListCard: FC<ToolCardProps> = ({ result }) => {
  const candidatesRaw = safeArray<CandidateItem>(result, 'candidates');
  const candidatesFromArray = asArray<CandidateItem>(result);
  const allCandidates: CandidateItem[] =
    candidatesRaw.length > 0 ? candidatesRaw : candidatesFromArray;
  const total = safeGet<number>(result, 'total') ?? allCandidates.length;
  const candidates = allCandidates.slice(0, MAX_DISPLAY);

  if (candidates.length === 0) return null;

  return (
    <CardShell wide>
      <div
        style={{
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          display: 'flex',
          gap: 10,
          padding: 18,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {candidates.map((candidate, i) => {
          const matchedSet = new Set(
            (candidate.matchedSkills ?? []).map((s) => s.toLowerCase()),
          );
          const allSkills = (candidate.skills ?? []).slice(0, 3);

          return (
            <div
              key={candidate.userId ?? i}
              style={{
                scrollSnapAlign: 'start',
                flexShrink: 0,
                width: 260,
                borderRadius: 20,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                position: 'relative',
                background: 'rgba(0,0,0,0.04)',
              }}
            >
              {/* Match score badge */}
              {candidate.matchScore != null && (
                <span
                  className="tabbio-menu-badge"
                  style={{
                    position: 'absolute',
                    top: 12,
                    insetInlineEnd: 12,
                  }}
                >
                  {Math.round(candidate.matchScore)}%
                </span>
              )}

              {/* Avatar + Name row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingInlineEnd: candidate.matchScore != null ? 40 : 0 }}>
                <img
                  src={
                    candidate.avatarUrl
                      ? (normalizeAvatarUrl(candidate.avatarUrl, 72) ?? candidate.avatarUrl)
                      : defaultAvatar
                  }
                  alt={candidate.name ?? 'Candidate'}
                  referrerPolicy="no-referrer"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                    background: 'rgba(0,0,0,0.06)',
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {candidate.name || 'Unknown'}
                </span>
              </div>

              {/* Title */}
              {candidate.title && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {candidate.title}
                </div>
              )}

              {/* Location */}
              {candidate.location && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <MaterialSymbol name="location_on" size={12} />
                  {candidate.location}
                </div>
              )}

              {/* Skills pills */}
              {allSkills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                  {allSkills.map((skill, si) => {
                    const isMatched = matchedSet.has(skill.toLowerCase());
                    return (
                      <span
                        key={si}
                        className="tabbio-menu-badge"
                        style={
                          isMatched
                            ? {
                                background: 'rgba(0,0,0,0.06)',
                                fontWeight: 600,
                              }
                            : undefined
                        }
                      >
                        {skill}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Open to work badge */}
              {candidate.openToWork && (
                <span className="tabbio-menu-badge" style={{ alignSelf: 'flex-start' }}>
                  Open to work
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '0 18px 14px',
          fontSize: 12,
          color: 'var(--text-tertiary)',
        }}
      >
        Showing {candidates.length} of {total} candidate{total !== 1 ? 's' : ''}
      </div>
    </CardShell>
  );
};
